/**
 * Conversione bidirezionale tra **date range giornaliero** e il vecchio
 * `period` ('1'..'12' | 'Q1'..'Q4' | 'YEAR') + `year` usato dalle dashboard
 * Area/Store (che internamente aggregano ancora a granularità mensile).
 *
 * La Panoramica è già daily-first end-to-end. Per le altre dashboard
 * teniamo un layer di adattamento: l'utente vede sempre il date picker,
 * ma sotto convertiamo a `(period, year)` per non riscrivere la pipeline
 * di aggregazione mensile. Il refactor "vero daily" su Area/Store è una
 * card P2 (non in questo round).
 *
 * Regole di rilevamento "range coincide con un periodo canonico":
 *  - Anno intero (1 gen → 31 dic stesso anno) → 'YEAR'
 *  - Quarter intero (3 mesi consecutivi che iniziano al mese 1/4/7/10
 *    del primo mese del trimestre) → 'Q1'..'Q4'
 *  - Mese intero (1° → ultimo giorno dello stesso mese) → '1'..'12'
 *  - Altrimenti: usiamo il mese di `dateFrom` come "rappresentativo" e
 *    restituiamo period = numero mese.
 */
import { parseISO, format, startOfMonth, endOfMonth, startOfYear, endOfYear } from 'date-fns';
import { it as itLocale } from 'date-fns/locale';

const QUARTER_START_MONTH = { Q1: 1, Q2: 4, Q3: 7, Q4: 10 };

/**
 * Converte un range ISO ("yyyy-MM-dd", "yyyy-MM-dd") in `{period, year}`.
 * Se il range non combacia con nessun periodo canonico, ritorna il mese
 * di `dateFrom` come period rappresentativo.
 */
export function rangeToPeriod(dateFrom, dateTo) {
  const f = parseISO(dateFrom);
  const t = parseISO(dateTo);
  const year = f.getFullYear();
  const fIso = format(f, 'yyyy-MM-dd');
  const tIso = format(t, 'yyyy-MM-dd');

  // Anno intero — stesso anno per entrambi gli estremi.
  if (
    t.getFullYear() === year &&
    fIso === format(startOfYear(f), 'yyyy-MM-dd') &&
    tIso === format(endOfYear(f), 'yyyy-MM-dd')
  ) {
    return { period: 'YEAR', year };
  }

  // Mese intero — stesso mese, primo→ultimo giorno.
  if (
    t.getFullYear() === year && t.getMonth() === f.getMonth() &&
    fIso === format(startOfMonth(f), 'yyyy-MM-dd') &&
    tIso === format(endOfMonth(f), 'yyyy-MM-dd')
  ) {
    return { period: String(f.getMonth() + 1), year };
  }

  // Quarter intero — primo giorno del mese di apertura → ultimo giorno
  // del mese di chiusura del trimestre.
  for (const [qLabel, startMonth] of Object.entries(QUARTER_START_MONTH)) {
    const qStart = new Date(year, startMonth - 1, 1);
    const qEnd = endOfMonth(new Date(year, startMonth + 1, 1));
    if (
      fIso === format(qStart, 'yyyy-MM-dd') &&
      tIso === format(qEnd, 'yyyy-MM-dd')
    ) {
      return { period: qLabel, year };
    }
  }

  // Fallback: mese di dateFrom come rappresentativo.
  return { period: String(f.getMonth() + 1), year };
}

/**
 * Converte `(period, year)` in `{from, to}` (entrambi ISO date string).
 * Inverso di `rangeToPeriod` per i casi canonici.
 */
export function periodToRange(period, year) {
  if (period === 'YEAR') {
    return {
      from: `${year}-01-01`,
      to: `${year}-12-31`,
    };
  }
  if (typeof period === 'string' && period.startsWith('Q')) {
    const startMonth = QUARTER_START_MONTH[period];
    if (!startMonth) return null;
    const qStart = new Date(year, startMonth - 1, 1);
    const qEnd = endOfMonth(new Date(year, startMonth + 1, 1));
    return {
      from: format(qStart, 'yyyy-MM-dd'),
      to: format(qEnd, 'yyyy-MM-dd'),
    };
  }
  const m = Number(period);
  if (m >= 1 && m <= 12) {
    const mStart = new Date(year, m - 1, 1);
    const mEnd = endOfMonth(mStart);
    return {
      from: format(mStart, 'yyyy-MM-dd'),
      to: format(mEnd, 'yyyy-MM-dd'),
    };
  }
  return null;
}

/**
 * Restituisce `true` se il range coincide esattamente con uno dei periodi
 * "canonici" (mese intero, quarter intero, anno intero). Usato dalla UI
 * per mostrare un hint "vista parziale" quando l'utente seleziona un
 * range custom che non quaglia con la granularità mensile.
 */
export function isCanonicalRange(dateFrom, dateTo) {
  if (!dateFrom || !dateTo) return true;
  const conv = rangeToPeriod(dateFrom, dateTo);
  const r = periodToRange(conv.period, conv.year);
  return !!(r && r.from === dateFrom && r.to === dateTo);
}

/**
 * Per range "parziali" (non canonici) ritorna una label leggibile che
 * spiega all'utente come il sistema sta aggregando sotto il cofano —
 * "5 giorni di marzo 2026". Ritorna `null` per range canonici.
 *
 * Avvertimento d'uso: la label esiste per chiarire all'AM che KPI a
 * granularità mensile (Health Score, Mesi in target, ecc.) sotto stanno
 * comunque cuocendo sull'intero mese di `dateFrom`. Non sostituisce un
 * "true daily-first" su Area/Store (card P2 separata).
 */
export function aggregationHint(dateFrom, dateTo) {
  if (!dateFrom || !dateTo) return null;
  if (isCanonicalRange(dateFrom, dateTo)) return null;
  const f = parseISO(dateFrom);
  const t = parseISO(dateTo);
  const days = Math.round((t.getTime() - f.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  const monthLabel = format(f, 'MMMM yyyy', { locale: itLocale });
  const crossMonth = f.getMonth() !== t.getMonth() || f.getFullYear() !== t.getFullYear();
  if (crossMonth) {
    return `Vista parziale · ${days} giorni · aggregato sul mese di ${monthLabel}`;
  }
  return `Vista parziale · ${days} giorni di ${monthLabel}`;
}
